import { Component } from "react";
import { observer } from "mobx-react";

import type { Message, ToolApprovalDecision } from "../models.ts";
import { ImgWarning } from "./Images/ImgWarning.tsx";

type ChatMessageToolApprovalProps = {
  message: Message;
  pendingToolApprovalName?: string | null;
  toolApprovalDecision?: ToolApprovalDecision | null;
  onToolApprovalDecision?: (toolName: string, decision: ToolApprovalDecision) => void;
};

@observer
export class ChatMessageToolApproval extends Component<ChatMessageToolApprovalProps> {
  render() {
    const { message, pendingToolApprovalName, onToolApprovalDecision } = this.props;

    // Approval state lives on the message itself (survives tool-call collapsing —
    // see getMessageChain which copies it onto the collapsed message).
    const { toolApprovalDecision, pendingToolApproval, toolApproval, isAborted } = message;
    const isPending = !!pendingToolApprovalName;

    // Decision: live (set on decision), persisted on the tool message (survives reload),
    // or a pending approval that ended in abort/cancel (isAborted on this message).
    // isAborted alone is NOT enough — regular tool errors also set it.
    const rawDecision =
      toolApprovalDecision ||
      (toolApproval === 'allow' ? 'allow' : toolApproval === 'deny' ? 'deny' : undefined) ||
      (pendingToolApproval && isAborted ? 'deny' : undefined);
    // 'always-allow' is an allow — normalise so the chip renders "Allowed".
    const decision: 'allow' | 'deny' | undefined =
      rawDecision === 'always-allow' ? 'allow' : rawDecision as 'allow' | 'deny' | undefined;

    // Nothing to show: no pending approval and no recorded decision
    if (!isPending && !decision) return null;

    // Decision recorded → summary chip
    if (decision && !isPending) {
      const toolName = message.toolCalls?.[0]?.name || message.toolsFunctionName;
      const decisionText = decision === 'allow' ? 'Allowed' : 'Denied';
      const decisionClass = decision === 'allow' ? 'approval-summary-allowed' : 'approval-summary-denied';

      return (
        <div className={`tool-approval-summary ${decisionClass}`}>
          <span className="tool-approval-summary-text">Tool {toolName}: {decisionText}</span>
        </div>
      );
    }

    if (!isPending || !pendingToolApproval) return null;

    return (
      <div className="tool-approval-container">
        <div className="tool-approval-header">
          <span className="tool-approval-icon"><ImgWarning size={16} /></span>
          <span className="tool-approval-title">MCP Tool Approval Required</span>
        </div>
        <div className="tool-approval-details">
          <div className="tool-approval-info">
            <span className="tool-approval-label">Server:</span>
            <span className="tool-approval-value">{pendingToolApproval.serverName}</span>
          </div>
          <div className="tool-approval-info">
            <span className="tool-approval-label">Tool:</span>
            <span className="tool-approval-value">{pendingToolApproval.toolName}</span>
          </div>
          {pendingToolApproval.arguments && (
            <div className="tool-approval-arguments">
              <span className="tool-approval-label">Arguments:</span>
              <pre className="tool-approval-args-code">{pendingToolApproval.arguments}</pre>
            </div>
          )}
        </div>
        <div className="tool-approval-actions">
          <button
            className="tool-approval-btn tool-approval-deny"
            onClick={() => onToolApprovalDecision?.(pendingToolApproval.toolName, 'deny')}
          >
            Deny
          </button>
          <button
            className="tool-approval-btn tool-approval-allow"
            onClick={() => onToolApprovalDecision?.(pendingToolApproval.toolName, 'allow')}
          >
            Allow
          </button>
          <button
            className="tool-approval-btn tool-approval-always"
            onClick={() => onToolApprovalDecision?.(pendingToolApproval.toolName, 'always-allow')}
          >
            Always Allow
          </button>
        </div>
      </div>
    );
  }
}
